import React, { Component, Fragment} from 'react';
import {bindActionCreators} from "redux";
import { connect } from 'react-redux';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import moment from 'moment';
import groupBy from "lodash/groupBy";
import { 
  GridComponent,
  Inject,
  Sort,
  ColumnsDirective,
  ColumnDirective 
} from '@syncfusion/ej2-react-grids';
import { CalendarComponent } from '@syncfusion/ej2-react-calendars';
import { Popover, PopoverBody, Button } from "reactstrap";
import i18n from 'i18next';

import {
  setShowFilterEventFields,
  setIsFilterDate,
  setIsSearchTopBar
} from "../../../actions/application";
import { getSearchEventList, getSearchDateEventList } from '../../api/calendar-api';
import Spinner from '../../../components/spinner/spinner';
import EventsSearchHeader from './EventsSearchHeader';
import '../../../app.scss';

/**
 * Extracts the calendar ID from a filename path
 * In IMAP, filename is the full path like /remote.php/dav/calendars/user/calendar-name/event.ics
 * CalendarId is the path without the event filename
 * @param {string} filename - Event filename
 * @returns {string} Calendar ID (path without the event filename, with trailing slash)
 */
const extractCalendarIdFromFilename = (filename) => {
  if (!filename) return '';
  const parts = filename.split('/');
  parts.pop(); // Remove event filename (e.g., event.ics)
  return parts.join('/') + '/';
};

/**
 * Formats a non-recurring event for the search grid
 * @param {Object} ev - The event from API
 * @param {Object} calendarInfo - Calendar info (id, summary)
 * @returns {Object} Formatted event for grid display
 */
const formatNonRecurringEvent = (ev, calendarInfo = null) => {
  return {
    ...ev,
    IsAllDay: ev.IsAllDay || ev.isAllDay || !ev.start.dateTime,
    _calendarId: calendarInfo?.id || ev.filename,
    _originalStart: ev.start,
    _originalEnd: ev.end,
  };
};

/**
 * Processes recurring events and generates occurrences for the grid
 * @param {Object} eventRecurrence - The recurring event from API
 * @param {Object} calendarInfo - Calendar info (id, summary)
 * @param {Object} scheduleObj - Syncfusion schedule object for generating occurrences
 * @returns {Array} Array of formatted occurrence events
 */
const processRecurringEvent = (eventRecurrence, calendarInfo, scheduleObj) => {
  const eventRecurrenceObj = {
    Id: eventRecurrence.id,
    Subject: eventRecurrence.summary,
    StartTime: eventRecurrence.start.dateTime,
    EndTime: eventRecurrence.end.dateTime,
    RecurrenceRule: eventRecurrence.recurrence[0].replace('RRULE:', ''),
    IsAllDay: eventRecurrence.IsAllDay || eventRecurrence.isAllDay || false,
  };

  const eventRecurrences = scheduleObj.eventBase.generateOccurrence(eventRecurrenceObj);
  
  return eventRecurrences.map(evr => ({
    id: evr.Id,
    filename: eventRecurrence.filename || evr.Id,
    summary: evr.Subject,
    start: {
      dateTime: evr.StartTime,
      timeZone: "UTC"
    },
    end: {
      dateTime: evr.EndTime,
      timeZone: "UTC"
    },
    _calendarId: calendarInfo?.id || eventRecurrence.filename,
    _originalStart: eventRecurrence.start,
    _originalEnd: eventRecurrence.end,
    recurrence: eventRecurrence.recurrence,
    IsAllDay: evr.IsAllDay || false,
    _isRecurrenceOccurrence: true,
  }));
};

/**
 * Processes all events from a calendar API response
 * @param {Object} eventsResult - Response from calendar API (result.items)
 * @param {Object} calendarInfo - Calendar info (id, summary)
 * @param {Object} scheduleObj - Syncfusion schedule object
 * @param {Function} filterFn - Optional filter function for recurrence occurrences
 * @returns {Array} Array of all processed events
 */
const processCalendarEvents = (eventsResult, calendarInfo, scheduleObj, filterFn = null) => {
  const events = eventsResult.items;
  const nonRecurringEvents = events.filter(x => !x.recurrence);
  const recurringEvents = events.filter(x => x.recurrence);

  // Process non-recurring events
  const formattedNonRecurring = nonRecurringEvents.map(ev => 
    formatNonRecurringEvent(ev, calendarInfo)
  );

  // Process recurring events
  let formattedRecurring = [];
  for (const eventRecurrence of recurringEvents) {
    const occurrences = processRecurringEvent(eventRecurrence, calendarInfo, scheduleObj);
    formattedRecurring = [...formattedRecurring, ...(filterFn ? occurrences.filter(filterFn) : occurrences)];
  }

  return [...formattedNonRecurring, ...formattedRecurring];
};

class EventsSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      startTime: null,
      endTime: null,
      isPopoverId: false,
      isDisabled: true,
      accounts: [],
      gridData: []
    };
    this.calendarEventGridRef = React.createRef(null);
    this.dateValues = React.createRef([]);
    this.calendarObj = React.createRef(null);
    this.dateValues.current = [];
    this.animationSettings = { effect: 'None' };
    this.position = { X: 'Right', Y: 'Top' };
    this.toasts = [
      { content: i18n.t("calendar.required_date_range"), cssClass: 'e-toast-danger', icon: 'e-error toast-icons' },
      { content: i18n.t("calendar.validate_date_range"), cssClass: 'e-toast-danger', icon: 'e-error toast-icons' }
    ];
  }
  toastCusAnimation = {
    hide: { duration: '1' },
    show: { duration: '200' }
  };

  componentDidMount() {
    this.calendarEventGridRef.current.style.display = 'none';
    const existPopoverId = document.querySelector('#filter-date-popover');
    if (existPopoverId) {
      this.setState({isPopoverId: true});
    }
    // Listen for event save to refresh search grid
    window.addEventListener('RefreshEventSearchGrid', this.handleRefreshSearchGrid);
  }

  componentWillUnmount() {
    window.removeEventListener('RefreshEventSearchGrid', this.handleRefreshSearchGrid);
  }

  handleRefreshSearchGrid = () => {
    // Only refresh if grid has data visible
    // Store the current state to avoid race conditions
    const hasGridData = this.state.gridData.length > 0;
    const searchParams = {
      startTime: this.state.startTime,
      endTime: this.state.endTime,
      keyword: this.props.eventFilterKeyword,
      calendars: this.props.calendars
    };
    
    if (hasGridData) {
      // Small delay to allow the save operation to complete
      setTimeout(() => {
        this.refreshSearchGrid(searchParams);
      }, 500);
    }
  }

  refreshSearchGrid = async (searchParams = null) => {
    // Use provided search params or current state (for backwards compatibility)
    const calendarsData = searchParams?.calendars || this.props.calendars;
    const calendars = groupBy(calendarsData, "checked");
    
    // Skip if no calendars are checked
    if (!calendars.true || calendars.true.length === 0) {
      return;
    }

    this.setState({ loading: true });
    const searchData = {
      dateStart: searchParams?.startTime ?? this.state.startTime,
      dateEnd: searchParams?.endTime ?? this.state.endTime,
      summary: searchParams?.keyword ?? this.props.eventFilterKeyword
    };

    // Build date range filter if a date-range search is active
    let dateRangeFilter = null;
    if (searchData.dateStart && searchData.dateEnd) {
      const dateStart = new Date(moment(searchData.dateStart).startOf("day").format("YYYY-MM-DDTHH:mm:ss"));
      const dateEnd = new Date(moment(searchData.dateEnd).endOf("day").format("YYYY-MM-DDTHH:mm:ss"));
      dateRangeFilter = (x) => {
        const start = new Date(x.start.dateTime);
        const end = new Date(x.end.dateTime);
        return (dateStart < start && end < dateEnd);
      };
    }

    try {
      let newEventData = [];
      const eventsData = await getSearchEventList(
        this.props.userId,
        this.props.mailAccount,
        this.props.lexonToken,
        searchData,
        calendars.true
      );

      const calendarInfo = null; // IMAP uses filename field for calendar identification
      const processedEvents = processCalendarEvents(eventsData.result, calendarInfo, this.props.scheduleObj, dateRangeFilter);
      newEventData = [...newEventData, ...processedEvents];

      this.setState({ gridData: newEventData.sort((a, b) => new Date(a.start.dateTime) - new Date(b.start.dateTime)), loading: false });
    } catch (error) {
      this.setState({ loading: false });
      console.error('Error refreshing search grid:', error);
    }
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.isSearchTopBar !== this.props.isSearchTopBar) {
      if (this.props.eventFilterKeyword !== '' && this.props.isSearchTopBar) {
        const searchData = {
          dateStart: this.state.startTime,
          dateEnd: this.state.endTime,
          summary: this.props.eventFilterKeyword
        };
        this.setState({loading: true});
        try {
          const calendars = groupBy(this.props.calendars, "checked");
          const eventsData = await getSearchEventList(
            this.props.userId,
            this.props.mailAccount,
            this.props.lexonToken,
            searchData,
            calendars.true
          );

          const calendarInfo = null; // IMAP uses filename field for calendar identification
          const newEventData = processCalendarEvents(eventsData.result, calendarInfo, this.props.scheduleObj);

          this.setState({loading: false});
          this.props.setIsSearchTopBar(false);
          this.showSearchEvents('show', newEventData);
        } catch {
          this.setState({loading: false});
        }
      } else {
        this.setState({loading: false});
      }
    }
    if (!this.props.isSearchTopBar &&
      this.props.eventFilterKeyword !== prevProps.eventFilterKeyword &&
      this.props.eventFilterKeyword === '' &&
      !this.props.isFilterDate) {
      this.showSearchEvents('hide');
    }
    if (prevProps.isFilterDate !== this.props.isFilterDate && this.props.isFilterDate === false) {
      this.setState({
        startTime: null,
        endTime: null
      });
      // Only hide grid if no keyword search is active or pending
      if (!this.props.eventFilterKeyword && !this.props.isSearchTopBar) {
        this.showSearchEvents('hide');
      }
    }
  }

  dialogFilterEventFieldsClose = () => {
    this.dateValues.current = [];
    this.setState({isDisabled: true, startTime: null, endTime: null});
    this.props.setShowFilterEventFields(false);
    this.props.setIsFilterDate(false);

    // If there's an active keyword, re-run the search without the date filter
    if (this.props.eventFilterKeyword) {
      this.props.setIsSearchTopBar(true);
    } else {
      this.showSearchEvents('hide');
    }
  }

  gridTemplate = props => {
    const calendars = groupBy(this.props.calendars, "checked");
    const nameCalendarFind = calendars.true.find(x => (
      decodeURIComponent(x.id.split('/')[5]) === decodeURIComponent(props?.filename.split('/')[5])
    ));
    return (
      <div>
        <span>{nameCalendarFind.summary}</span>
      </div>
    );
  };

  gridStartDateTemplate = props => (
    <div>
      <span>
        {
          props.IsAllDay ? moment(props.start.dateTime).format('DD/MM/YYYY') :
            moment(props.start.dateTime).format('DD/MM/YYYY HH:mm')
        }
      </span>
    </div>
  );

  gridEndDateTemplate = props => (
    <div>
      <span>
        {
          props.IsAllDay ? moment(props.end.dateTime).hour(-2).format('DD/MM/YYYY') :
            moment(props.end.dateTime).format('DD/MM/YYYY HH:mm')
        }
      </span>
    </div>
  );

  showSearchEvents(type, data) {
    if (type === 'show') {
      this.setState({ gridData: data });
      this.calendarEventGridRef.current.style.display = 'block';
      if (this.props.scheduleObj.element) {
        this.props.scheduleObj.element.style.display = 'none';
      }
    } else {
      // Only refresh if grid was actually visible (had data) - prevents unnecessary refresh on each keystroke
      const wasGridVisible = this.state.gridData.length > 0;
      this.setState({ gridData: [] });
      if (this.props.scheduleObj.element) {
        this.props.scheduleObj.element.style.display = 'block';
        this.calendarEventGridRef.current.style.display = 'none';
        // Refresh the scheduler to recalculate event layout after being hidden
        // This prevents events from showing as "1 More", "2 More" etc.
        if (wasGridVisible) {
          setTimeout(() => {
            this.props.scheduleObj.refresh();
          }, 100);
        }
      }
    }
  }

  onRenderDayCell = (args) => {
    const iso = args.date.toISOString().split('T')[0];
    args.element.setAttribute('data-date', iso);
    if (this.dateValues.current.length === 2) {
      const timestamps = this.dateValues.current.map(d => {
        const n = new Date(d); n.setHours(0, 0, 0, 0); return n.getTime();
      });
      const minTs = Math.min(...timestamps);
      const maxTs = Math.max(...timestamps);
      const cellTs = new Date(iso).getTime();
      if (cellTs > minTs && cellTs < maxTs) {
        args.element.classList.add('e-range-between');
      }
    }
  }

  applyRangeHighlight = () => {
    const popoverContainer = document.querySelector('.popover');
    if (!popoverContainer) return;
    popoverContainer.querySelectorAll('td.e-range-between').forEach(el => el.classList.remove('e-range-between'));
    if (this.dateValues.current.length !== 2) return;
    const timestamps = this.dateValues.current.map(d => {
      const n = new Date(d); n.setHours(0, 0, 0, 0); return n.getTime();
    });
    const minTs = Math.min(...timestamps);
    const maxTs = Math.max(...timestamps);
    popoverContainer.querySelectorAll('td[data-date]').forEach(cell => {
      const cellTs = new Date(cell.getAttribute('data-date')).getTime();
      if (cellTs > minTs && cellTs < maxTs) {
        cell.classList.add('e-range-between');
      }
    });
  }

  handleSelectDate (event) {
    const popoverContainer = document.querySelector('.popover');
    if (event.values.length === 0) {
      this.dateValues.current = [];
    }
    if (event.values.length === 3) {
      event.values.shift();
    }
    if (event.values.length >= 2) {
      const removedFirstValues = event.values.shift();
      event.values = [removedFirstValues, ...event.values];
      this.dateValues.current = [...event.values];
    }

    if (popoverContainer.querySelectorAll('.e-selected').length >= 2) {
      const selectedDates = popoverContainer.querySelectorAll('.e-selected');
      selectedDates.forEach((date, index) => {
        if (index >= 2) {
          date.classList.remove('e-selected');
        }
      });
    }
    if (this.dateValues.current.length === 2 && this.state.isDisabled) {
      this.setState({isDisabled: false});
    }
    if (this.dateValues.current.length === 0 && !this.state.isDisabled) {
      this.setState({isDisabled: true});
    }
    setTimeout(() => this.applyRangeHighlight(), 0);
  }

    searchOnclick = async eventFilterKeyword => {
      if (this.dateValues.current.length >= 2) {
        const minimumDate = new Date(Math.min.apply(null, this.dateValues.current));
        const maximumDate = new Date(Math.max.apply(null, this.dateValues.current));
        this.setState({
          loading: true,
          startTime: minimumDate,
          endTime: maximumDate
        });
        this.props.setShowFilterEventFields(false);
        this.props.setIsFilterDate(true);
        const searchData = {
          summary: eventFilterKeyword,
          dateStart: minimumDate,
          dateEnd: maximumDate
        };
        const dateStart = new Date(moment(searchData.dateStart).startOf("day").format("YYYY-MM-DDTHH:mm:ss"));
        const dateEnd = new Date(moment(searchData.dateEnd).endOf("day").format("YYYY-MM-DDTHH:mm:ss"));

        // Filter function to only include recurrence occurrences within date range
        const dateRangeFilter = (x) => {
          const start = new Date(x.start.dateTime);
          const end = new Date(x.end.dateTime);
          return (dateStart < start && end < dateEnd);
        };

        try {
          let newEventData = [];
          if (eventFilterKeyword !== '') {
            const calendars = groupBy(this.props.calendars, "checked");
            const eventsData = await getSearchEventList(
              this.props.userId,
              this.props.mailAccount,
              this.props.lexonToken,
              searchData,
              calendars.true
            );

            const calendarInfo = null; // IMAP uses filename field for calendar identification
            const processedEvents = processCalendarEvents(
              eventsData.result,
              calendarInfo,
              this.props.scheduleObj,
              dateRangeFilter
            );
            newEventData = [...newEventData, ...processedEvents];
          } else {
            const calendars = groupBy(this.props.calendars, "checked");
            const searchDate = {
              dateStart: minimumDate,
              dateEnd: maximumDate
            };
            for (const calendar of calendars.true) {
              const eventsData = await getSearchDateEventList(
                calendar.id,
                this.props.userId,
                this.props.mailAccount,
                this.props.lexonToken,
                searchDate
              );

              const calendarInfo = { id: calendar.id, summary: calendar.summary };
              const processedEvents = processCalendarEvents(
                eventsData.result,
                calendarInfo,
                this.props.scheduleObj,
                dateRangeFilter
              );
              newEventData = [...newEventData, ...processedEvents];
            }
          }
          this.showSearchEvents('show', newEventData);
          this.setState({loading: false});
        } catch {
          this.setState({loading: false});
        }
      } else {
        this.toastObj.show(this.toasts[0]);
      }
    }

  clearOnClick = () => {
    if (this.props.scheduleObj.element) {
      this.props.scheduleObj.element.style.display = 'block';
      document.getElementById('form-search').reset();
      this.showSearchEvents('hide');
    }
  }

  exportGridData = () => {
    const { gridData } = this.state;
    if (!gridData.length) {
      return;
    }

    const csvHeaders = ['Asunto', 'Calendario', 'Inicio', 'Fin', 'Ubicacion', 'Descripcion'];
    const escapeCsvValue = (value = '') => `"${String(value).replace(/"/g, '""')}"`;

    const csvRows = gridData.map((event) => (
      [
        event.summary,
        event.calendarName,
        event.start?.dateTime,
        event.end?.dateTime,
        event.location,
        event.description,
      ].map(escapeCsvValue).join(',')
    ));

    const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = csvUrl;
    downloadLink.download = 'resultados-busqueda-eventos.csv';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(csvUrl);
  }

    /**
     * Builds scheduleEventData from the grid row, using the scheduler's data source
     * as the primary source of truth (to include all fields like LexON classification,
     * filename, Visibility, ImageName, IdEventLexon, etc.) with a manual fallback.
     */
    buildScheduleEventData(eventData) {
      const isRecurrence = eventData._isRecurrenceOccurrence || !!eventData.recurrence;

      // Primary: use the scheduler's data source which contains all fields
      // In IMAP, the event Id in the scheduler is the filename (full path to .ics file)
      const existingEvent = this.props.scheduleObj.dataModule?.dataManager?.dataSource?.json?.find(
        e => e.Id == eventData.filename
      );

      if (existingEvent) {
        return {
          ...existingEvent,
          ...(isRecurrence && { RecurrenceID: eventData.filename }),
        };
      }

      // Fallback: build manually from grid data if event not found in scheduler
      const calendarId = extractCalendarIdFromFilename(eventData.filename);
      const startTime = eventData._originalStart?.dateTime
        ? new Date(eventData._originalStart.dateTime)
        : new Date(eventData._originalStart?.date);
      const endTime = eventData._originalEnd?.dateTime
        ? new Date(eventData._originalEnd.dateTime)
        : new Date(eventData._originalEnd?.date);

      return {
        Id: eventData.filename,
        CalendarId: calendarId,
        Subject: eventData.summary,
        Location: eventData.location || '',
        Description: eventData.description || '',
        StartTime: startTime,
        EndTime: endTime,
        IsAllDay: eventData.IsAllDay || false,
        RecurrenceRule: isRecurrence && eventData.recurrence
          ? eventData.recurrence[0].replace('RRULE:', '') : null,
        ...(isRecurrence && { RecurrenceID: eventData.filename }),
        Attendees: eventData.attendees || [],
        Reminders: eventData.reminders,
      };
    }

    onGridRowSelected = (args) => {
      const eventData = args.data;

      // Skip if event doesn't have required data
      if (!eventData._originalStart || !eventData._originalEnd) {
        return;
      }

      const scheduleEventData = this.buildScheduleEventData(eventData);
      const isRecurrence = eventData._isRecurrenceOccurrence || !!eventData.recurrence;

      // If it's a recurrence occurrence, dispatch event to show confirmation modal in calendar.jsx
      if (isRecurrence) {
        window.dispatchEvent(new CustomEvent('OpenRecurrenceConfirmationFromGrid', {
          detail: { scheduleEventData }
        }));
        return;
      }

      // Open editor for non-recurring events
      // Keep grid visible so user can select another event after closing the modal
      setTimeout(() => {
        this.props.scheduleObj.openEditor(scheduleEventData, 'Save');
      }, 100);
    }

    render() {
      const { showFilterEventFields, eventFilterKeyword } = this.props;
      const { isPopoverId, isDisabled, gridData } = this.state;
      return (
        <Fragment>
          <Spinner
            visible={this.state.loading}
            className={`spinnerCalendar`}
            pathClassName={`spinnerPath`}
          />
          <div id="calendar-event-grid" ref={this.calendarEventGridRef}>
            <EventsSearchHeader onExport={this.exportGridData} />
            {gridData && gridData.length >= 0 && (
              <GridComponent
                dataSource={gridData.sort((a, b) => a.start.dateTime - b.start.dateTime)}
                width="auto"
                allowSorting={true}
                rowSelected={(args) => this.onGridRowSelected(args)}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="summary"
                    headerText={i18n.t("calendar.subject")}
                    width={120}
                  />
                  <ColumnDirective
                    headerText={i18n.t("tooltips.calendar")}
                    width={120}
                    template={this.gridTemplate}
                  />
                  <ColumnDirective
                    field="start.dateTime"
                    headerText={i18n.t("calendar.startTime")}
                    width={120}
                    format={{ type: 'dateTime', format: 'd/M/y HH:mm' }}
                    template={this.gridStartDateTemplate}
                  />
                  <ColumnDirective
                    field="end.dateTime"
                    headerText={i18n.t("calendar.endTime")}
                    width={120}
                    format={{ type: 'dateTime', format: 'd/M/y HH:mm' }}
                    template={this.gridEndDateTemplate}
                  />
                </ColumnsDirective>
                <Inject services={[Sort]} />
              </GridComponent>
            )}
          </div>
          {isPopoverId && (
            <Popover
              trigger="legacy"
              isOpen={showFilterEventFields}
              target="filter-date-popover"
              toggle={() => {
                this.props.setShowFilterEventFields(false);
              }}
              placement="bottom"
              className="popover-filter-date-events"
            >
              <PopoverBody>
                <div>
                  <div className="mt-4">
                    <form className="event-search" id="form-search">
                      <CalendarComponent
                        id="calendar"
                        ref={calendarObj => {
                          this.calendarObj.current = calendarObj;
                        }}
                        isMultiSelection={true}
                        firstDayOfWeek={1}
                        showTodayButton={false}
                        change={event => {
                          this.handleSelectDate(event);
                        }}
                        dayHeaderFormat="Abbreviated"
                        values={this.dateValues.current}
                        renderDayCell={(args) => this.onRenderDayCell(args)}
                      />
                    </form>
                  </div>
                  <div className="d-flex justify-content-end mt-4 event-search-btn-container">
                    <Button
                      className="font-weight-bold btn-outline-primary"
                      title={i18n.t("header.clear")}
                      color="secondary"
                      disabled={isDisabled}
                      onClick={() => this.dialogFilterEventFieldsClose()}
                    >
                      {i18n.t("header.clear")}
                    </Button>
                    <Button
                      className="mr-auto font-weight-bold ml-1"
                      color="primary"
                      title={i18n.t("header.apply")}
                      disabled={isDisabled}
                      onClick={() => this.searchOnclick(eventFilterKeyword)}
                    >
                      {i18n.t("header.apply")}
                    </Button>
                  </div>
                </div>
              </PopoverBody>
            </Popover>
          )}
          <ToastComponent
            ref={toast => {
              this.toastObj = toast;
            }}
            id='toast_pos'
            position={this.position}
            animation={this.toastCusAnimation}
            timeOut={2000}
          >
          </ToastComponent>
        </Fragment>
      );
    }
}

const mapStateToProps = state => ({
  eventFilterKeyword: state.application.eventFilterKeyword,
  showFilterEventFields: state.application.showFilterEventFields,
  isFilterDate: state.application.isFilterDate,
  isSearchTopBar: state.application.isSearchTopBar
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      setShowFilterEventFields: showFilterEventFields => dispatch(setShowFilterEventFields(showFilterEventFields)),
      setIsFilterDate: isFilterDate => dispatch(setIsFilterDate(isFilterDate)),
      setIsSearchTopBar: isSearchTopBar => dispatch(setIsSearchTopBar(isSearchTopBar))
    },
    dispatch
  )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)((EventsSearch));
